import { TemplateTag } from "./templateTags"


// Determine wheter the frontend is in the dutch locale
export const isDutchLocale = (locale?: string | null): boolean => {
    const normalizedLocale = locale?.replace('_', '-').toLowerCase();
    return normalizedLocale === 'nl' || normalizedLocale?.startsWith('nl-') === true;
};


// Get the preferred tag key by language
export const getPreferredTagKey = (tag: TemplateTag, locale?: string | null): string => {
    let key = tag.key;

    if (isDutchLocale(locale) && tag.nlKey) {
        key = tag.nlKey;
    }

    return key;
};


// Get all tag keys
export const getAllTagKeys = (tag: TemplateTag): string[] => {
    const keys = [tag.key];

    if (tag.nlKey) {
        keys.push(tag.nlKey);
    }

    if (tag.aliasKeys) {
        keys.push(...tag.aliasKeys);
    }

    return keys;
};


// Map the tags to its data
export const mapTagsToNestedData = (tags: TemplateTag[], getNestedData: (objectData: any) => any): TemplateTag[] => {
    return tags.map(tag => ({
        ...tag,
        getValue: (objectData, context) => tag.getValue(getNestedData(objectData), context)
    }));
};


// Get the client person of a job
export const getJobClientPerson = (objectData: any): any | null => {
    let person = null;

    if (objectData?.client?.entity_type === 'person') {
        person = objectData.client;
    } else if (objectData?.client?.entity_type !== 'person' && objectData?.reporting_person) {
        person = objectData.reporting_person;
    }

    return person;
};


// Get the location person of a job
export const getJobLocationPerson = (objectData: any): any | null => {
    let person = null;

    if (objectData?.location_person) {
        person = objectData.location_person;
    } else if (objectData?.client?.entity_type === 'person') {
        person = objectData.client;
    }

    return person;
};


// Get the person of a contact page, the contact itself when it is a person or its first linked person
export const getContactPerson = (objectData: any): any | null => {
    let person = null;

    if (objectData?.entity_type === 'person') {
        person = objectData;
    } else if (Array.isArray(objectData?.persons) && objectData.persons.length > 0) {
        person = objectData.persons[0];
    }

    return person;
};


// Get the location of a contact page, the first primary location of a contact or the location page object itself
export const getContactLocation = (objectData: any): any | null => {
    let location = null;

    if (Array.isArray(objectData?.primary_locations)) {
        location = objectData.primary_locations[0] ?? null;
    } else if (objectData?.street !== undefined || objectData?.address !== undefined) {
        location = objectData;
    }

    return location;
};


// Get the appointments data from the object data
export const getAppointments = (objectData: any): any[] => {
    if (Array.isArray(objectData?.appointments)) {
        return objectData.appointments;
    }
    return [];
};


// Sort the appointments on date
export const getSortedAppointments = (objectData: any): any[] => {
    return [...getAppointments(objectData)]
        .filter(appointment => appointment?.start_datetime && appointment.deleted !== true)
        .sort((a, b) => {
            return new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime();
        });
};


// Get the next appointment
export const getNextAppointment = (objectData: any): any | null => {
    const now = new Date();

    const appointment = getSortedAppointments(objectData).find(appointment => {
        return new Date(appointment.start_datetime) >= now && appointment.status !== 'done';
    });

    return appointment ?? null;
};


// Get the last done appointment
export const getLastDoneAppointment = (objectData: any): any | null => {
    const appointments = getSortedAppointments(objectData).filter(appointment => {
        return appointment.status === 'done';
    });

    return appointments.length > 0 ? appointments[appointments.length - 1] : null;
};


// Get all scheduled appointments
export const getScheduledAppointments = (objectData: any): any[] => {
    return getSortedAppointments(objectData).filter(appointment => {
        return appointment.status === 'scheduled' || appointment.status === 'reserved';
    });
};


// Get the assignee name(s) of (an) appointment(s)
export const getAppointmentAssignees = (appointment: any): string => {
    let assignees = '';

    if (Array.isArray(appointment?.assignees)) {
        assignees = appointment.assignees
            .map((assignee: any) => assignee.full_name ?? `${assignee.first_name ?? ''} ${assignee.last_name ?? ''}`.trim())
            .filter(Boolean)
            .join(', ');
    }

    return assignees;
};